'use client'

import React, { useState, useEffect, useRef } from 'react'
import Image from 'next/image'
import type { Media } from '@/payload-types'
import { RichText } from '../RichText'
import { DynamicIcon } from '../DynamicIcon'
import { PersonaSectionButton } from '../PersonaSectionButton'
import { resolveSectionButton } from '@/lib/persona-sections'
import { getMediaUrl } from '../../lib/media'

interface HowWeHelpSection {
  heading: string
  content: any
  image?: Media | string | null
  iconName?: string | null
  buttonText?: string | null
  buttonLink?: string | null
  id?: string
}

interface PersonaStickyScrollProps {
  sections?: HowWeHelpSection[]
}

export function PersonaStickyScroll({ sections }: PersonaStickyScrollProps) {
  const [activeIndex, setActiveIndex] = useState(0)
  const sectionRefs = useRef<(HTMLDivElement | null)[]>([])

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + window.innerHeight / 2

      // Find which section is currently in view
      sectionRefs.current.forEach((ref, index) => {
        if (ref) {
          const rect = ref.getBoundingClientRect()
          const elementTop = rect.top + window.scrollY
          const elementBottom = elementTop + rect.height

          if (scrollPosition >= elementTop && scrollPosition <= elementBottom) {
            setActiveIndex(index)
          }
        }
      })
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    handleScroll() // Initial call
    return () => window.removeEventListener('scroll', handleScroll)
  }, [sections?.length])

  if (!sections || sections.length === 0) {
    return null
  }

  return (
    <section className="py-12 md:py-20 px-6 md:px-12">
      <div className="max-w-380 mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16">
          {/* Sticky Image Column with Progress Indicator */}
          <div className="sticky top-32 h-[80vh] self-start hidden lg:block">
            <div className="relative h-full">
              {/* Image container with transitions */}
              {sections.map((section, index) => {
                const sectionImage = typeof section.image === 'object' ? section.image : null
                const imageSrc = sectionImage
                  ? getMediaUrl(sectionImage, 'card', { cacheBust: true }) || null
                  : null
                const imageAlt = sectionImage?.alt || section.heading

                return (
                  <div
                    key={section.id || index}
                    className={`absolute inset-0 transition-opacity duration-700 ${
                      activeIndex === index ? 'opacity-100' : 'opacity-0'
                    }`}
                  >
                    {imageSrc ? (
                      <div className="relative h-full rounded-2xl overflow-hidden">
                        <Image
                          src={imageSrc}
                          alt={imageAlt}
                          fill
                          className="object-cover"
                        />
                        {/* Gradient overlay for better progress bar visibility */}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
                      </div>
                    ) : (
                      <div className="h-full rounded-2xl bg-gray-200 flex items-center justify-center">
                        <p className="text-gray-500">Image placeholder</p>
                      </div>
                    )}
                  </div>
                )
              })}

              {/* Progress Indicator */}
              <div className="absolute bottom-8 left-8 right-8 z-10">
                {/* Section dots */}
                <div className="flex items-center gap-3 mb-4">
                  {sections.map((_, index) => (
                    <div
                      key={index}
                      className="flex-1 h-1 bg-white/30 rounded-full overflow-hidden"
                    >
                      <div
                        className="h-full bg-brand-gold transition-all duration-500 rounded-full"
                        style={{
                          width: activeIndex >= index ? '100%' : '0%',
                        }}
                      />
                    </div>
                  ))}
                </div>

                {/* Current section indicator */}
                <div className="text-white text-sm font-medium">
                  <span className="text-brand-gold">{activeIndex + 1}</span> / {sections.length}
                </div>
              </div>
            </div>
          </div>

          {/* Scrolling Content Column */}
          <div className="space-y-0">
            {sections.map((section, index) => {
              const button = resolveSectionButton(section)

              return (
                <div
                  key={section.id || index}
                  ref={(el) => {
                    sectionRefs.current[index] = el
                  }}
                  className="min-h-[80vh] flex items-center py-12"
                >
                  <div className="space-y-6 w-full">
                    {/* Icon */}
                    {section.iconName && (
                      <DynamicIcon
                        name={section.iconName}
                        className="w-10 h-10 md:w-12 md:h-12 text-brand-gold mb-4"
                        strokeWidth={1.5}
                      />
                    )}

                    {/* Heading with fade-in effect */}
                    <h3
                      className={`text-xl md:text-2xl lg:text-3xl font-medium text-gray-900 transition-all duration-700 ${
                        activeIndex === index
                          ? 'opacity-100 translate-y-0'
                          : 'opacity-50 translate-y-4'
                      }`}
                    >
                      {section.heading}
                    </h3>

                    {/* Content with fade-in effect */}
                    <div
                      className={`transition-all duration-700 delay-100 ${
                        activeIndex === index
                          ? 'opacity-100 translate-y-0'
                          : 'opacity-50 translate-y-4'
                      }`}
                    >
                      <RichText
                        data={section.content}
                        className="prose prose-xl max-w-none text-gray-700 leading-relaxed [&_p]:mb-4 [&_ul]:mb-4 [&_ol]:mb-4 [&_li]:mb-2 [&_strong]:text-brand-blue [&_em]:italic [&_em]:text-brand-gold"
                      />
                    </div>

                    {/* Optional CMS-configured button, same label and destination as the banner */}
                    {button && (
                      <div
                        className={`transition-all duration-700 delay-200 ${
                          activeIndex === index
                            ? 'opacity-100 translate-y-0'
                            : 'opacity-50 translate-y-4'
                        }`}
                      >
                        <PersonaSectionButton button={button} />
                      </div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </section>
  )
}
